import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { MasteryState } from '@coffeeplexity/shared';
import type {
  AIProgressInsight,
  SkillGapAnalysis,
  LearningEfficiencyMetrics,
  InsightRecommendation,
} from '../../components/ai/ProgressInsightsPanel';
import type {
  SkillGap,
  SkillDependency,
  GapRecommendation,
} from '../../components/ai/SkillGapAnalyzer';
import type {
  LearningEfficiencyData,
  OptimizationSuggestion,
  LearningPattern,
} from '../../components/ai/LearningEfficiencyTracker';
import type {
  ProgressTrendData,
  ProgressPrediction,
  TrendAnomaly,
} from '../../components/ai/ProgressTrendAnalyzer';

import { logger } from '../../utils/logger';
import type {
  ProgressShareData,
  PrivacySettings,
  ShareTemplate,
} from '../../components/ai/ProgressSharingPanel';

export interface ProgressInsightsState {
  // AI Insights
  insights: AIProgressInsight[];
  insightFilters: {
    type: 'all' | 'improvement' | 'skill_gap' | 'efficiency' | 'trend';
    priority: 'all' | 'high' | 'medium' | 'low';
  };

  // Skill Gap Analysis
  skillGaps: SkillGap[];
  skillDependencies: SkillDependency[];
  targetSkillLevels: Record<string, number>;

  // Learning Efficiency
  efficiencyData: LearningEfficiencyData | null;
  appliedOptimizations: string[];

  // Progress Trends
  trendData: ProgressTrendData | null;
  trendPredictions: ProgressPrediction[];
  trendAnomalies: TrendAnomaly[];

  // Progress Sharing
  shareHistory: ProgressShareData[];
  privacySettings: PrivacySettings;
  shareTemplates: ShareTemplate[];

  // UI State
  activeView:
    | 'overview'
    | 'insights'
    | 'gaps'
    | 'efficiency'
    | 'trends'
    | 'sharing';
  expandedInsights: string[];
  selectedSkill: string | null;
  selectedTimeRange: '7d' | '30d' | '90d' | '1y';

  // Loading States
  isLoadingInsights: boolean;
  isLoadingGaps: boolean;
  isLoadingEfficiency: boolean;
  isLoadingTrends: boolean;
  isGeneratingShare: boolean;

  // Error Handling
  error: string | null;
  lastUpdated: string | null;
}

const initialState: ProgressInsightsState = {
  insights: [],
  insightFilters: {
    type: 'all',
    priority: 'all',
  },
  skillGaps: [],
  skillDependencies: [],
  targetSkillLevels: {},
  efficiencyData: null,
  appliedOptimizations: [],
  trendData: null,
  trendPredictions: [],
  trendAnomalies: [],
  shareHistory: [],
  privacySettings: {
    defaultVisibility: 'friends',
    allowAnonymousSharing: true,
    includePersonalData: false,
    allowComments: true,
    allowReactions: true,
    autoExpireShares: true,
    expireDays: 30,
    notifyOnInteraction: true,
    shareAnalytics: true,
  },
  shareTemplates: [],
  activeView: 'overview',
  expandedInsights: [],
  selectedSkill: null,
  selectedTimeRange: '30d',
  isLoadingInsights: false,
  isLoadingGaps: false,
  isLoadingEfficiency: false,
  isLoadingTrends: false,
  isGeneratingShare: false,
  error: null,
  lastUpdated: null,
};

// Async thunks
export const generateProgressInsights = createAsyncThunk(
  'progressInsights/generateInsights',
  async (params: {
    userId: string;
    masteryStates: MasteryState[];
    timeRange: '7d' | '30d' | '90d' | '1y';
  }) => {
    // This would call the AI service to generate insights
    // For now, return mock data
    const mockInsights: AIProgressInsight[] = [
      {
        id: `insight_${Date.now()}`,
        type: 'improvement',
        title: 'Optimize Your Morning Learning',
        description:
          'Your retention rate is 23% higher when learning between 8-10 AM',
        aiReasoning:
          'Analysis of your learning sessions shows consistent patterns of better performance and retention during morning hours.',
        confidence: 0.87,
        impact: 'high',
        priority: 1,
        actionable: true,
        recommendations: [
          {
            id: 'rec_1',
            type: 'schedule',
            title: 'Schedule Daily Morning Sessions',
            description: 'Block 30 minutes each morning for focused learning',
            estimatedImpact: 0.23,
            timeRequired: 30,
            difficulty: 0.3,
          },
        ],
        relatedSkills: ['espresso-basics', 'milk-steaming'],
        generatedAt: new Date().toISOString(),
      },
    ];

    return mockInsights;
  }
);

export const analyzeSkillGaps = createAsyncThunk(
  'progressInsights/analyzeGaps',
  async (params: {
    userId: string;
    masteryStates: MasteryState[];
    targetLevels: Record<string, number>;
  }) => {
    // This would call the AI service to analyze skill gaps
    const mockGaps: SkillGap[] = [
      {
        skillId: 'latte-art',
        skillName: 'Latte Art',
        category: 'Presentation',
        currentLevel: 0.45,
        targetLevel: 0.8,
        gap: 0.35,
        priority: 'high',
        blockedSkills: ['advanced-presentation'],
        prerequisites: ['milk-steaming'],
        estimatedTimeToClose: 1200,
        difficulty: 0.7,
        aiRecommendations: [],
        learningPath: [],
      },
    ];

    return mockGaps;
  }
);

export const calculateLearningEfficiency = createAsyncThunk(
  'progressInsights/calculateEfficiency',
  async (params: {
    userId: string;
    timeRange: '7d' | '30d' | '90d' | '1y';
  }) => {
    // This would call the AI service to calculate efficiency metrics
    const mockEfficiencyData: LearningEfficiencyData = {
      userId: params.userId,
      timeRange: params.timeRange,
      overallEfficiency: 0.73,
      metrics: [],
      trends: [],
      optimizations: [],
      patterns: [],
      predictions: [],
      lastUpdated: new Date().toISOString(),
    };

    return mockEfficiencyData;
  }
);

export const analyzeTrends = createAsyncThunk(
  'progressInsights/analyzeTrends',
  async (params: {
    userId: string;
    masteryStates: MasteryState[];
    timeRange: '7d' | '30d' | '90d' | '1y';
  }) => {
    // This would call the AI service to analyze trends
    const mockTrendData: ProgressTrendData = {
      userId: params.userId,
      timeRange: params.timeRange,
      overallTrend: {
        direction: 'upward',
        velocity: 0.02,
        acceleration: 0.001,
        confidence: 0.85,
        r2Score: 0.78,
        seasonality: null,
        breakpoints: [],
      },
      skillTrends: [],
      categoryTrends: [],
      predictions: [],
      milestones: [],
      patterns: [],
      anomalies: [],
      lastUpdated: new Date().toISOString(),
    };

    return mockTrendData;
  }
);

export const createProgressShare = createAsyncThunk(
  'progressInsights/createShare',
  async (shareData: Partial<ProgressShareData>) => {
    // This would call the API to create a share
    const newShare: ProgressShareData = {
      ...shareData,
      shareId: shareData.shareId || `share_${Date.now()}`,
      userId: shareData.userId || 'current_user',
      createdAt: new Date().toISOString(),
      analytics: {
        views: 0,
        interactions: 0,
        shares: 0,
        comments: 0,
        reactions: [],
      },
    } as ProgressShareData;

    return newShare;
  }
);

const progressInsightsSlice = createSlice({
  name: 'progressInsights',
  initialState,
  reducers: {
    // View Management
    setActiveView: (
      state,
      action: PayloadAction<ProgressInsightsState['activeView']>
    ) => {
      state.activeView = action.payload;
    },

    setSelectedSkill: (state, action: PayloadAction<string | null>) => {
      state.selectedSkill = action.payload;
    },

    setSelectedTimeRange: (
      state,
      action: PayloadAction<'7d' | '30d' | '90d' | '1y'>
    ) => {
      state.selectedTimeRange = action.payload;
    },

    // Insight Management
    toggleInsightExpansion: (state, action: PayloadAction<string>) => {
      const insightId = action.payload;
      const index = state.expandedInsights.indexOf(insightId);
      if (index > -1) {
        state.expandedInsights.splice(index, 1);
      } else {
        state.expandedInsights.push(insightId);
      }
    },

    dismissInsight: (state, action: PayloadAction<string>) => {
      state.insights = state.insights.filter(
        insight => insight.id !== action.payload
      );
    },

    applyInsightRecommendation: (
      state,
      action: PayloadAction<{
        insightId: string;
        recommendationId: string;
      }>
    ) => {
      const insight = state.insights.find(
        i => i.id === action.payload.insightId
      );
      if (insight) {
        const recommendation = insight.recommendations.find(
          r => r.id === action.payload.recommendationId
        );
        if (recommendation) {
          // Mark as applied (in real implementation, this would trigger API calls)
          logger.log('Applied recommendation:', recommendation);
        }
      }
    },

    // Filter Management
    setInsightFilters: (
      state,
      action: PayloadAction<Partial<ProgressInsightsState['insightFilters']>>
    ) => {
      state.insightFilters = { ...state.insightFilters, ...action.payload };
    },

    // Skill Gap Management
    setTargetSkillLevel: (
      state,
      action: PayloadAction<{ skillId: string; level: number }>
    ) => {
      state.targetSkillLevels[action.payload.skillId] = action.payload.level;
    },

    focusOnSkillGap: (state, action: PayloadAction<string>) => {
      state.selectedSkill = action.payload;
      state.activeView = 'gaps';
    },

    // Efficiency Management
    applyOptimization: (state, action: PayloadAction<string>) => {
      if (!state.appliedOptimizations.includes(action.payload)) {
        state.appliedOptimizations.push(action.payload);
      }
    },

    removeOptimization: (state, action: PayloadAction<string>) => {
      state.appliedOptimizations = state.appliedOptimizations.filter(
        id => id !== action.payload
      );
    },

    // Sharing Management
    updatePrivacySettings: (
      state,
      action: PayloadAction<Partial<PrivacySettings>>
    ) => {
      state.privacySettings = { ...state.privacySettings, ...action.payload };
    },

    deleteShare: (state, action: PayloadAction<string>) => {
      state.shareHistory = state.shareHistory.filter(
        share => share.shareId !== action.payload
      );
    },

    updateShareAnalytics: (
      state,
      action: PayloadAction<{
        shareId: string;
        analytics: Partial<ProgressShareData['analytics']>;
      }>
    ) => {
      const share = state.shareHistory.find(
        s => s.shareId === action.payload.shareId
      );
      if (share) {
        share.analytics = { ...share.analytics, ...action.payload.analytics };
      }
    },

    // Error Handling
    clearError: state => {
      state.error = null;
    },

    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },

    // Data Refresh
    refreshAllData: state => {
      state.lastUpdated = new Date().toISOString();
      // This would trigger re-fetching of all data
    },
  },
  extraReducers: builder => {
    // Generate Insights
    builder
      .addCase(generateProgressInsights.pending, state => {
        state.isLoadingInsights = true;
        state.error = null;
      })
      .addCase(generateProgressInsights.fulfilled, (state, action) => {
        state.isLoadingInsights = false;
        state.insights = action.payload;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(generateProgressInsights.rejected, (state, action) => {
        state.isLoadingInsights = false;
        state.error = action.error.message || 'Failed to generate insights';
      });

    // Analyze Skill Gaps
    builder
      .addCase(analyzeSkillGaps.pending, state => {
        state.isLoadingGaps = true;
        state.error = null;
      })
      .addCase(analyzeSkillGaps.fulfilled, (state, action) => {
        state.isLoadingGaps = false;
        state.skillGaps = action.payload;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(analyzeSkillGaps.rejected, (state, action) => {
        state.isLoadingGaps = false;
        state.error = action.error.message || 'Failed to analyze skill gaps';
      });

    // Calculate Efficiency
    builder
      .addCase(calculateLearningEfficiency.pending, state => {
        state.isLoadingEfficiency = true;
        state.error = null;
      })
      .addCase(calculateLearningEfficiency.fulfilled, (state, action) => {
        state.isLoadingEfficiency = false;
        state.efficiencyData = action.payload;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(calculateLearningEfficiency.rejected, (state, action) => {
        state.isLoadingEfficiency = false;
        state.error = action.error.message || 'Failed to calculate efficiency';
      });

    // Analyze Trends
    builder
      .addCase(analyzeTrends.pending, state => {
        state.isLoadingTrends = true;
        state.error = null;
      })
      .addCase(analyzeTrends.fulfilled, (state, action) => {
        state.isLoadingTrends = false;
        state.trendData = action.payload;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(analyzeTrends.rejected, (state, action) => {
        state.isLoadingTrends = false;
        state.error = action.error.message || 'Failed to analyze trends';
      });

    // Create Share
    builder
      .addCase(createProgressShare.pending, state => {
        state.isGeneratingShare = true;
        state.error = null;
      })
      .addCase(createProgressShare.fulfilled, (state, action) => {
        state.isGeneratingShare = false;
        state.shareHistory.unshift(action.payload);
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(createProgressShare.rejected, (state, action) => {
        state.isGeneratingShare = false;
        state.error = action.error.message || 'Failed to create share';
      });
  },
});

export const {
  setActiveView,
  setSelectedSkill,
  setSelectedTimeRange,
  toggleInsightExpansion,
  dismissInsight,
  applyInsightRecommendation,
  setInsightFilters,
  setTargetSkillLevel,
  focusOnSkillGap,
  applyOptimization,
  removeOptimization,
  updatePrivacySettings,
  deleteShare,
  updateShareAnalytics,
  clearError,
  setError,
  refreshAllData,
} = progressInsightsSlice.actions;

export default progressInsightsSlice.reducer;

// Selectors
export const selectProgressInsights = (state: {
  progressInsights: ProgressInsightsState;
}) => state.progressInsights.insights;

export const selectFilteredInsights = (state: {
  progressInsights: ProgressInsightsState;
}) => {
  const { insights, insightFilters } = state.progressInsights;

  let filtered = insights;

  if (insightFilters.type !== 'all') {
    filtered = filtered.filter(insight => insight.type === insightFilters.type);
  }

  if (insightFilters.priority !== 'all') {
    filtered = filtered.filter(
      insight => insight.impact === insightFilters.priority
    );
  }

  return filtered.sort((a, b) => {
    const priorityWeight = { high: 3, medium: 2, low: 1 };
    const aPriority = priorityWeight[a.impact] * a.confidence;
    const bPriority = priorityWeight[b.impact] * b.confidence;
    return bPriority - aPriority;
  });
};

export const selectSkillGaps = (state: {
  progressInsights: ProgressInsightsState;
}) => state.progressInsights.skillGaps;

export const selectEfficiencyData = (state: {
  progressInsights: ProgressInsightsState;
}) => state.progressInsights.efficiencyData;

export const selectTrendData = (state: {
  progressInsights: ProgressInsightsState;
}) => state.progressInsights.trendData;

export const selectShareHistory = (state: {
  progressInsights: ProgressInsightsState;
}) => state.progressInsights.shareHistory;

export const selectPrivacySettings = (state: {
  progressInsights: ProgressInsightsState;
}) => state.progressInsights.privacySettings;

export const selectActiveView = (state: {
  progressInsights: ProgressInsightsState;
}) => state.progressInsights.activeView;

export const selectSelectedSkill = (state: {
  progressInsights: ProgressInsightsState;
}) => state.progressInsights.selectedSkill;

export const selectLoadingStates = (state: {
  progressInsights: ProgressInsightsState;
}) => ({
  isLoadingInsights: state.progressInsights.isLoadingInsights,
  isLoadingGaps: state.progressInsights.isLoadingGaps,
  isLoadingEfficiency: state.progressInsights.isLoadingEfficiency,
  isLoadingTrends: state.progressInsights.isLoadingTrends,
  isGeneratingShare: state.progressInsights.isGeneratingShare,
});

export const selectInsightFilters = (state: {
  progressInsights: ProgressInsightsState;
}) => state.progressInsights.insightFilters;

export const selectExpandedInsights = (state: {
  progressInsights: ProgressInsightsState;
}) => state.progressInsights.expandedInsights;
